import React, { useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { theme } from 'antd';
import { LineChartOutlined, WarningOutlined } from '@ant-design/icons';
import moment from 'moment';
import { EPButton } from '@/components';
import PublishInsightIcon from '@/comp/common/icons/PublishInsightIcon';
import SalesforceIcon from '@/comp/common/icons/SalesforceIcon';
import { CTACard, CTACardContainer } from '@/comp/common/CTACard';
import { useStores } from '@/hooks/use-stores';
import { syncDatesFromUrl, syncMetricsFromUrl } from '@/hooks/useInvestigationSync';
import {
  getFormattedSfsContract,
  getCareProgramIcon,
  shouldDisplayCareStatus,
} from '@/comp/MapBuilder/nodes/nodeWidget/DetailsDrawer/PackageCareIndication/PackageCareIndication.utils';
import { Entity } from '@/comp/MapBuilder/nodes/nodeWidget/NodeWidget.types';
import type { InsightWidgetParams } from '@/types/chatbot.types';
import * as S from './InsightResponseWidget.styles';
import {
  ValueBreakdownItem,
  ValueBreakdownLine,
  ValueBreakdownText,
} from '@/comp/pages/AIAssistantPage/AssistantInsights.styles';
import { CareIconWrapper, ValueChip, ValueText } from '@/comp/pages/AIAssistantPage/AssistantInsightCard.styles';
import { toCurrencySymbol } from '@/utils/currency.utils';

const { useToken } = theme;

export interface InsightResponseData {
  id: string;
  deviceId?: string;
  title: string;
  status: string;
  severity: 'risk' | 'maintenance' | 'energy';
  value: number;
  currency: string;
  date: string;
  description: string;
  recommendedAction: string;
}

const InsightResponseWidget: React.FC<InsightWidgetParams> = observer(
  ({
    content,
    onAnimationComplete,
    shouldStart = true,
    isStreaming = false,
    onPublishClick,
    onViewTrendClick,
    onViewAlarmsClick,
  }: InsightWidgetParams) => {
    const { t } = useTranslation();
    const { token } = useToken();
    const { devicesStore, stationStore, investigationStore } = useStores();
    const navigate = useNavigate();
    const paramsFromUrl = useParams<{ stationId?: string }>();
    const currentStationId = paramsFromUrl.stationId || stationStore?.currentStation?.id;
    const [insightData, setInsightData] = useState<InsightResponseData | null>(null);

    useEffect(() => {
      if (!content) {
        setInsightData(null);
        return;
      }

      try {
        // Parse content if it's a string, otherwise use directly
        const parsed = typeof content === 'string' ? JSON.parse(content) : content;

        // Merge with existing data if available (for chunked content)
        setInsightData(prevData => {
          if (prevData) {
            return { ...prevData, ...parsed };
          }
          return parsed as InsightResponseData;
        });
      } catch (error) {
        console.error('Failed to parse insight content:', error);
        // Don't reset to null on error - keep existing data for chunked updates
      }
    }, [content]);

    useEffect(() => {
      if (shouldStart && insightData && onAnimationComplete && !isStreaming) {
        // Call completion after a short delay to allow rendering
        // Only complete when not streaming (i.e., when all chunks are received)
        const timer = setTimeout(() => {
          onAnimationComplete();
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [shouldStart, insightData, onAnimationComplete, isStreaming]);

    // Get device name from deviceId (must be called before early return)
    // Access observable arrays directly so MobX can track changes
    const deviceName = useMemo(() => {
      if (!insightData?.deviceId || !devicesStore) return null;
      const device = devicesStore.getDeviceById(insightData.deviceId);
      const assetName = device?.name;

      // If device name is 'system' or matches station name, show "Station Level"
      if (assetName && assetName !== 'system' && assetName !== stationStore?.currentStation?.name) {
        return assetName;
      }
      return t('StationLevel');
    }, [insightData?.deviceId, devicesStore, devicesStore?.devicesMetadata, stationStore?.currentStation?.name, t]);

    // Get device metadata and asset for care icon logic (must be called before early return)
    // Access observable arrays directly so MobX can track changes
    const deviceMetadata = useMemo(() => {
      if (!insightData?.deviceId || !devicesStore) return null;
      return devicesStore.getDeviceById(insightData.deviceId);
    }, [insightData?.deviceId, devicesStore, devicesStore?.devicesMetadata]);

    const currentAsset = useMemo(() => {
      if (!insightData?.deviceId || !stationStore?.assetsDevices) return null;
      return stationStore.assetsDevices.find(a => a.deviceId === insightData.deviceId) || null;
    }, [insightData?.deviceId, stationStore?.assetsDevices]);

    // Construct Entity-like object for shouldDisplayCareStatus
    const entity = useMemo((): Entity | null => {
      if (!deviceMetadata) return null;
      return {
        deviceId: deviceMetadata.id,
        deviceType: deviceMetadata.deviceTypes?.[0] || deviceMetadata.deviceType,
        modelDisplayName: deviceMetadata.modelDisplayName || deviceMetadata.name,
        name: deviceMetadata.name,
        brand: deviceMetadata.brand || '',
        controllerBrand: deviceMetadata.controllerBrand || '',
        controllerId: deviceMetadata.controllerId || '',
        controllerModel: deviceMetadata.controllerModel || '',
        deviceControllerName: deviceMetadata.deviceControllerName || '',
        deviceKind: deviceMetadata.deviceKind,
        elementIndex: 0,
        isIndependent: false,
        primaryPressureSensor: '',
        primaryFlowSensor: '',
        onDevices: [],
      };
    }, [deviceMetadata]);

    const formattedSfsContract = useMemo(() => {
      if (!currentAsset?.SfsContract) return null;
      return getFormattedSfsContract(currentAsset.SfsContract);
    }, [currentAsset?.SfsContract]);

    // Use the same logic as asset drawer to determine if care should be displayed
    const shouldShowCareIcon = useMemo(() => {
      // Only show for devices (not station-level insights)
      if (!insightData?.deviceId || !entity || !currentAsset) return false;

      // Use the same logic as PackageCareIndication component
      const displayCareStatus = shouldDisplayCareStatus(currentAsset, entity, t);
      if (!displayCareStatus) return false;

      // Check if care program exists and is valid
      if (!formattedSfsContract) return false;
      const isNotCareAble = formattedSfsContract.includes('notcareable');
      const isNoCareProgram = formattedSfsContract.includes('nocareprogram');
      return !isNotCareAble && !isNoCareProgram;
    }, [insightData?.deviceId, entity, currentAsset, formattedSfsContract, t]);

    // Get the appropriate care program icon
    const CareProgramIcon = useMemo(() => {
      if (!formattedSfsContract || !shouldShowCareIcon) return null;
      return getCareProgramIcon(formattedSfsContract);
    }, [formattedSfsContract, shouldShowCareIcon]);

    // Show loading state if we have partial data but not enough to render
    if (!insightData || !insightData.title) {
      return null;
    }

    const getTypeLabel = () => {
      switch (insightData.severity) {
        case 'risk':
          return t('Risk');
        case 'maintenance':
          return t('Maintenance');
        case 'energy':
          return t('Energy');
        default:
          return t('Risk');
      }
    };

    const formattedDate = insightData.date ? moment(insightData.date).format('MMM DD, YYYY') : '';
    const currencySymbol = toCurrencySymbol(insightData.currency);
    const formattedValue = insightData.value !== undefined ? insightData.value.toLocaleString() : '';

    const handlePublishClick = () => {
      if (onPublishClick && insightData.id) {
        onPublishClick(insightData.id);
      }
    };

    const handleViewTrendClick = () => {
      console.log('handleViewTrendClick', insightData);
      if (onViewTrendClick && insightData.id) {
        onViewTrendClick(insightData.id);
        return;
      }

      if (!currentStationId || !insightData) return;

      const insightDate = insightData.date ? moment(insightData.date) : moment();
      const startDate = insightDate.clone().startOf('day');
      const endDate = insightDate.clone().endOf('day');

      const metricId = insightData.deviceId ? `${insightData.deviceId}:pressureBar` : '';

      const params = new URLSearchParams();
      params.set('timestamp1Start', startDate.valueOf().toString());
      params.set('timestamp1End', endDate.valueOf().toString());
      if (metricId) {
        params.set('metricId', metricId);
      }

      if (investigationStore.isReady) {
        console.log('syncDatesFromUrl', params);
        syncDatesFromUrl(investigationStore, params);
        if (metricId) {
          console.log('syncMetricsFromUrl', params.get('metricId'));
          syncMetricsFromUrl(investigationStore, params.get('metricId'));
        }
      }

      navigate(`/home/station/${currentStationId}/tab/investigation`);
      // navigate(`/home/station/${currentStationId}/tab/investigation?${params.toString()}`);
    };

    const handleViewAlarmsClick = () => {
      if (onViewAlarmsClick && insightData.id) {
        onViewAlarmsClick(insightData.id);
        return;
      }

      if (!currentStationId || !insightData?.deviceId) return;

      const params = new URLSearchParams();
      params.set('deviceId', insightData.deviceId);

      navigate(`/home/station/${currentStationId}/tab/map?${params.toString()}`);
    };

    // Format description with markdown-like support
    const formatDescription = (text?: string) => {
      if (!text) return null;
      const lines = text.split('\n');
      return lines.map((line, index) => {
        // Check if line starts with bullet point
        if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
          return (
            <li key={index} style={{ marginBottom: token.marginXS }}>
              {line.replace(/^[-•]\s*/, '')}
            </li>
          );
        }
        return (
          <React.Fragment key={index}>
            {line}
            {index < lines.length - 1 && <br />}
          </React.Fragment>
        );
      });
    };

    // Format recommended actions
    const formatRecommendedActions = (text?: string) => {
      if (!text) return null;
      const lines = text.split('\n');
      return lines
        .map((line, index) => {
          const trimmed = line.trim();
          if (!trimmed) return null;

          // Check if line starts with bullet point or dash
          if (trimmed.startsWith('-') || trimmed.startsWith('•')) {
            return (
              <li key={index} style={{ marginBottom: token.marginXS }}>
                {trimmed.replace(/^[-•]\s*/, '')}
              </li>
            );
          }
          return (
            <React.Fragment key={index}>
              {line}
              {index < lines.length - 1 && <br />}
            </React.Fragment>
          );
        })
        .filter(Boolean);
    };

    return (
      <S.InsightResponseContainer>
        <S.InsightHeader>
          <S.InsightTitle>{insightData.title}</S.InsightTitle>
          <S.InsightMetadata>
            <ValueBreakdownItem $type={insightData.severity} $showBackground={false}>
              <ValueBreakdownLine $type={insightData.severity} />
              <ValueBreakdownText $type={insightData.severity}>{getTypeLabel()}</ValueBreakdownText>
            </ValueBreakdownItem>
            {deviceName && (
              <>
                <S.MetadataDot />
                <S.MetadataText>{deviceName}</S.MetadataText>
              </>
            )}
            {shouldShowCareIcon && CareProgramIcon && (
              <>
                <S.MetadataDot />
                <CareIconWrapper>
                  <CareProgramIcon width={48} height={20} />
                </CareIconWrapper>
              </>
            )}
            {formattedDate && (
              <>
                <S.MetadataDot />
                <S.MetadataText>{formattedDate}</S.MetadataText>
              </>
            )}
            {formattedValue && (
              <>
                <S.MetadataDot />
                <ValueChip>
                  <ValueText>
                    {currencySymbol}
                    {formattedValue}
                  </ValueText>
                </ValueChip>
              </>
            )}
          </S.InsightMetadata>
        </S.InsightHeader>

        {insightData.description && (
          <S.InsightDescription>
            <S.DescriptionText>{formatDescription(insightData.description)}</S.DescriptionText>
          </S.InsightDescription>
        )}

        {/* Action buttons appear only after description is complete */}
        {insightData.description && (
          <S.ActionButtonsContainer>
            <EPButton
              type="text"
              icon={<LineChartOutlined />}
              onClick={handleViewTrendClick}
              style={{ color: token.colorPrimary, border: 'none', background: 'transparent' }}
            >
              {t('View trend')}
            </EPButton>
            <EPButton
              type="text"
              icon={<WarningOutlined />}
              onClick={handleViewAlarmsClick}
              style={{ color: token.colorPrimary, border: 'none', background: 'transparent' }}
            >
              {t('View Alarms')}
            </EPButton>
          </S.ActionButtonsContainer>
        )}

        {/* Recommended Actions section appears only when recommendedAction is available */}
        {insightData.recommendedAction && (
          <S.RecommendedActionsSection>
            <S.RecommendedActionsTitle>{t('Recommended Actions')}</S.RecommendedActionsTitle>
            <S.RecommendedActionsList>
              {formatRecommendedActions(insightData.recommendedAction)}
            </S.RecommendedActionsList>
          </S.RecommendedActionsSection>
        )}

        {/* Call-to-action buttons appear only after recommended actions are complete */}
        {insightData.recommendedAction && (
          <CTACardContainer>
            <CTACard
              icon={<PublishInsightIcon width={48} height={48} />}
              title={t('Publish Insight to customer')}
              subtitle={t('Propose preventive maintenance or cooling system cleaning')}
              buttonText={
                <>
                  <span>{t('Review')}</span>
                  <span>& {t('Publish')}</span>
                </>
              }
              onClick={handlePublishClick}
            />
            <CTACard
              icon={<SalesforceIcon width={61} height={43} />}
              title={t('Open Opportunity in Salesforce')}
              buttonText={
                <>
                  <span>{t('Open in')}</span>
                  <span>{t('Salesforce')}</span>
                </>
              }
              onClick={() => {
                // TODO: Implement Salesforce integration
                console.log('Open in Salesforce clicked');
              }}
            />
          </CTACardContainer>
        )}
      </S.InsightResponseContainer>
    );
  },
);

InsightResponseWidget.displayName = 'InsightResponseWidget';

export default InsightResponseWidget;
